<template>
    <div class="profile-container">
        <h2>{{$t('profile.profile')}}</h2>
        <div class="upload-box">
            <div class="user-avatar">
                <img :src="user_pic" alt="user avatar">
            </div>
            <div class="extra-upload">
                <div class="inline-block">
                    <el-button
                        size="small"
                        type="primary"
                        @click="uploadAvatar"
                        :loading="btnController.uploadAvatar"
                    >{{$t('profile.upload_avatar')}}</el-button>
                    <input
                        type="file"
                        class="upload-file"
                        @change="uploadTrigger"
                        accept="image/gif, image/jpeg, image/png"
                    >
                </div>
                <!-- <el-button size="small" type="plain" @click="removeAvatar">delete</el-button> -->
                <div class="upload-tip">{{$t('profile.upload_limit')}}</div>
            </div>
        </div>

        <el-form
            :model="emailForm"
            :rules="emailRules"
            ref="emailForm"
            label-width="130px"
            @submit.native.prevent
            class="basic-form"
        >
            <el-form-item :label="$t('profile.email')" prop="email">
                <el-input type="email" v-model="emailForm.email" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item
                :label="$t('profile.verification_code')"
                prop="emailCode"
                class="form-item-align"
                v-if="validateFields.display_email_verification_input"
            >
                <el-input
                    type="text"
                    v-model="emailForm.emailCode"
                    class="inline-input"
                    autocomplete="off"
                    :placeholder="$t('profile.email_placeholder')"
                    maxlength="6"
                ></el-input>
                <el-button
                    class="setTimeOut-style"
                    v-if="!validateFields.emailCountingDown"
                    @click="sendCode('email')"
                >{{$t('profile.send_code')}}</el-button>
                <el-button
                    class="setTimeOut-style"
                    v-if="validateFields.emailCountingDown"
                    disabled
                >{{validateFields.emailField}}</el-button>
            </el-form-item>

            <div class="input-hint-plain" v-if="validateFields.display_email">
                <b>Email change:</b> Enter the verification code sent to your new email.
            </div>

            <div
                class="input-hint-plain"
                v-if="validateFields.display_email_verification_input"
                v-html="$t('profile.email_hint')"
            ></div>
        </el-form>

        <el-form
            :model="phoneForm"
            :rules="phoneRules"
            ref="phoneForm"
            label-width="130px"
            @submit.native.prevent
            class="basic-form"
        >
            <el-form-item :label="$t('profile.phone')">
                <vue-tel-input
                    v-model="phoneForm.phoneNumber"
                    :inputOptions="{ showDialCode: false }"
                    :preferredCountries="['us', 'cn', 'ca']"
                    :enabledCountryCode="false"
                    @onInput="onTelInput"
                />
                <div
                    v-if="validateFields.phoneIllegal"
                    class="el-form-item__error"
                >{{$t('profile.phone_illegal')}}</div>
            </el-form-item>

            <el-form-item
                :label="$t('profile.verification_code')"
                prop="phoneCode"
                class="form-item-align"
                v-if="validateFields.display_phone_verification_input"
            >
                <el-input
                    type="text"
                    v-model="phoneForm.phoneCode"
                    class="inline-input"
                    autocomplete="off"
                    :placeholder="$t('profile.phone_placeholder')"
                    maxlength="6"
                ></el-input>
                <el-button
                    class="setTimeOut-style"
                    @click="sendCode('phone')"
                    v-if="!validateFields.phoneCountingDown"
                >{{$t('profile.send_code')}}</el-button>
                <el-button
                    class="setTimeOut-style"
                    v-if="validateFields.phoneCountingDown"
                    disabled
                >{{validateFields.phoneField}}</el-button>
            </el-form-item>

            <div class="input-hint-plain" v-if="validateFields.display_phone">
                <b>Phone number change</b>: Enter the verification code sent to your new phone number.
            </div>

            <div
                class="input-hint-plain"
                v-if="validateFields.display_phone_verification_input"
                v-html="$t('profile.phone_hint')"
            ></div>
        </el-form>

        <el-form
            :model="basicForm"
            :rules="basicRules"
            ref="basicForm"
            label-width="130px"
            class="basic-form border-bottom"
        >
            <el-form-item :label="$t('profile.first_name')" prop="first_name">
                <el-input type="text" v-model="basicForm.first_name" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item :label="$t('profile.last_name')" prop="last_name">
                <el-input type="text" v-model="basicForm.last_name" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item :label="$t('profile.date_of_birth')" prop="date" class="date-of-birth">
                <div class="block">
                    <el-date-picker
                        v-model="basicForm.birth"
                        type="date"
                        value-format="yyyy-MM-dd"
                        :placeholder="$t('profile.select_date')"
                        :picker-options="pickerOptions"
                    ></el-date-picker>
                </div>
            </el-form-item>

            <el-form-item :label="$t('profile.gender')" prop="gender">
                <el-select
                    v-model="basicForm.gender"
                    :placeholder="$t('profile.select_placeholder')"
                    class="fromDataOptions"
                >
                    <el-option
                        v-for="item in fromDataOptions.gender"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                    ></el-option>
                </el-select>
            </el-form-item>

            <el-form-item :label="$t('profile.city_of_residence')" prop="cityResidence">
                <el-input type="text" v-model="basicForm.cityResidence" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item :label="$t('profile.nationality')" prop="nationalityName">
                <el-select
                    v-model="basicForm.nationality"
                    :placeholder="$t('profile.select_placeholder')"
                >
                    <el-option
                        v-for="(item, index) in nationalityList"
                        :key="index"
                        :label="item.name"
                        :value="item.code2"
                    ></el-option>
                </el-select>
            </el-form-item>

            <el-form-item class="text-right">
                <el-button
                    type="primary"
                    class="commitBtn"
                    @click="submitForm('basicForm')"
                    :loading="btnController.basicForm"
                >{{$t('profile.save_changes')}}</el-button>
            </el-form-item>
        </el-form>

        <el-form
            v-if="customer_level == 'WEB_TRIAL' "
            :model="membershipForm"
            :rules="membershipRules"
            ref="membershipForm"
            label-width="130px"
            class="basic-form border-bottom invitation-form"
        >
            <h3>{{$t("profile.upgrade_title")}}</h3>
            <p class="subtitle">{{$t("profile.upgrade_subtitle")}}</p>
            <el-form-item :label="$t('UJ.section10.invitation_code')" prop="invitation_code">
                <el-input
                    type="text"
                    v-model="membershipForm.invitation_code"
                    :placeholder="$t('UJ.section10.enter_code')"
                    autocomplete="off"
                ></el-input>
            </el-form-item>

            <el-form-item class="text-right">
                <el-button
                    type="primary"
                    class="commitBtn"
                    @click="submitForm('membershipForm')"
                    :loading="btnController.membershipForm"
                >{{$t('profile.upgrade')}}</el-button>
            </el-form-item>
        </el-form>

        <el-form
            :model="pwdForm"
            :rules="pwdRules"
            ref="pwdForm"
            label-width="130px"
            class="pwd-form"
        >
            <h3>{{$t('profile.password')}}</h3>
            <el-form-item :label="$t('profile.current_pwd')" prop="oldPassword">
                <el-input type="password" v-model="pwdForm.oldPassword" autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item :label="$t('profile.new_wd')" prop="password">
                <el-input type="password" v-model="pwdForm.password" autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item :label="$t('profile.confirm_pwd')" prop="checkPassword">
                <el-input type="password" v-model="pwdForm.checkPassword" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item class="text-right">
                <el-button
                    type="primary"
                    class="commitBtn"
                    @click="submitForm('pwdForm')"
                    :loading="btnController.pwdForm"
                >{{$t('profile.save_changes')}}</el-button>
            </el-form-item>
        </el-form>
    </div>
</template>

<script>
import { Message } from "element-ui";
import { Loading } from "element-ui";

export default {
    name: "profileContainer",
    props: {
        userProfile: Object,
        nationalities: Array
    },
    data() {
        let user_pic = require("../../../../website_20/static/img/icon-account-aside-defaultUserAvatar.svg");
        const $t = this.$t.bind(this);
        var validatePass = (rule, value, callback) => {
            if (value === "") {
                callback(new Error($t("profile.password_empty")));
            }
            if (rule.fullField == "oldPassword") {
                callback();
            }

            if (
                rule.fullField == "password" &&
                this.pwdForm.password != "" &&
                this.pwdForm.checkPassword != ""
            ) {
                this.$refs.pwdForm.validateField("checkPassword");
            }

            if (
                rule.fullField == "checkPassword" &&
                value !== this.pwdForm.password
            ) {
                callback(new Error($t("profile.password_diff")));
            } else {
                callback();
            }
        };

        var validateCode = (rule, value, callback) => {
            if (value === "" || value.length !== 6) return;

            let params = {
                id: this.userId,
                code:
                    rule.field == "phoneCode"
                        ? this.phoneForm.phoneCode
                        : this.emailForm.emailCode
            };
            if (rule.field == "phoneCode" && value.length == 6) {
                params.phoneNumber = this.phoneNumberInput;
            } else if (rule.field == "emailCode" && value.length == 6) {
                params.username = this.emailForm.email;
            }

            // const loading = this.$loading({
            //     lock: true,
            //     text: 'Sending Verfication Code',
            //     spinner: 'el-icon-loading',
            //     background: 'rgba(0, 0, 0, 0.7)'
            // });

            // const loading = this.$loading({
            //     lock: true,
            //     text: 'Sending Verification Code',
            //     spinner: 'el-icon-loading',
            //     background: 'rgba(0, 0, 0, 0.7)'
            // });

            this.$http
                .put(
                    `${this.ppbApi}api/user/${this.userId}`,
                    params,
                    this.requestConfig
                )
                .then(rep => {
                    // loading.close();
                    if (rep.status == 200) {
                        if (rule.field == "phoneCode") {
                            this.triggerMessage("success", {
                                message: $t("profile.phone_success")
                            });

                            this.clearCountDown(this.timeCountDown);
                            this.validateFields.phoneCountingDown = false;
                            this.validateFields.display_phone_verification_input = true;
                        } else {
                            this.triggerMessage("success", {
                                message: $t("profile.email_success")
                            });
                            this.clearCountDown(this.timeCountDown);
                            this.validateFields.emailCountingDown = false;
                            this.validateFields.display_email_verification_input = false;
                        }
                    } else {
                        this.triggerMessage("error", {
                            message: $t("profile.verification_error")
                        });
                    }
                })
                .catch(error => {
                    // loading.close();
                    this.triggerMessage("error", {
                        message: error.data[Object.keys(error.data)].toString()
                    });
                });
        };

        var validateEmail = (rule, value, callback) => {
            if (value === "") {
                callback(new Error($t("profile.email_empty")));
            }
            if (value != this.ruleFormCopy.email) {
                this.validateFields.display_email_verification_input = true;
            } else {
                this.validateFields.display_email_verification_input = false;
            }
        };

        return {
            customer_level: "WEB_TRIAL",
            nationalityList: this.nationalities,
            pickerOptions: {
                disabledDate(time) {
                    return time.getTime() > Date.now();
                }
            },
            userId: this.$root.userId,
            token: this.$root.token,
            ppbApi: this.$root.ppbApi,
            reqUrl: "",
            requestConfig: {
                headers: {}
            },

            btnController: {
                uploadAvatar: false,
                basicForm: false,
                membershipForm: false,
                pwdForm: false
            },

            user_pic: user_pic,

            validateFields: {
                display_email_verification_input: false,
                display_phone_verification_input: false,
                phoneIllegal: false,
                phoneField: "",
                emailField: "",
                phoneCountingDown: false,
                emailCountingDown: false
            },

            duplicateFields: {
                phoneNumber: "",
                email: "",
                nationality: "",
                nationalityName: ""
            },

            phoneNumberInput: "",
            preferredCountries: ["CN", "US", "CA"],
            actionUrl: "",

            fileReader: "",

            fromDataOptions: {
                gender: [
                    {
                        value: "F",
                        label: $t("profile.female")
                    },
                    {
                        value: "M",
                        label: $t("profile.male")
                    }
                ],
                select: ""
            },

            emailForm: {
                email: ""
            },
            emailRules: {
                email: [
                    {
                        type: "email",
                        required: true,
                        message: $t("profile.email_empty"),
                        trigger: ["blur", "change"]
                    },
                    {
                        type: "email",
                        message: $t("profile.email_illegal"),
                        trigger: ["blur", "change"]
                    },
                    {
                        validator: validateEmail
                    }
                ],
                emailCode: [
                    {
                        validator: validateCode,
                        trigger: "change"
                    }
                ]
            },

            phoneForm: {
                phoneNumber: " "
            },
            phoneRules: {
                phone: [
                    {
                        required: true,
                        message: $t("profile.phone_empty"),
                        trigger: "blur"
                    }
                ],
                phoneCode: [
                    {
                        validator: validateCode,
                        trigger: "change"
                    }
                ]
            },

            ruleFormCopy: {},
            basicForm: {
                verifCode: "",
                first_name: "",
                last_name: "",
                birth: "",
                gender: "",
                cityResidence: "",
                nationalityName: ""
            },
            basicRules: {
                first_name: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.first_name"),
                        trigger: "blur"
                    }
                ],
                last_name: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.last_name"),
                        trigger: "blur"
                    }
                ],
                birth: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.birth"),
                        trigger: "blur"
                    }
                ],
                gender: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.gender"),
                        trigger: "blur"
                    }
                ],
                cityResidence: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.cityResidence"),
                        trigger: "blur"
                    }
                ],
                nationalityName: [
                    {
                        required: true,
                        message: $t("profile.empty_msg.nationality"),
                        trigger: "blur"
                    }
                ]
            },

            membershipForm: {
                invitation_code: ""
            },
            membershipRules: {
                invitation_code: [
                    {
                        required: true,
                        message: $t("UJ.section10.inputIsEmpty"),
                        trigger: "blur"
                    }
                ]
            },

            pwdForm: {
                oldPassword: "",
                password: "",
                checkPassword: ""
            },
            pwdRules: {
                oldPassword: [{ validator: validatePass, trigger: "blur" }],
                password: [{ validator: validatePass, trigger: "blur" }],
                checkPassword: [{ validator: validatePass, trigger: "blur" }]
            },

            uploadedFiles: [],
            uploadError: null,
            currentStatus: null,
            uploadFieldName: "photos"
        };
    },

    beforeMount() {
        this.getUser = `${this.ppbApi}api/user/${this.userId}/`;
        this.useCode = `${this.ppbApi}api/vipcode/upgrade/`;
        this.reqUrl = `${this.ppbApi}api/user/${this.userId}/settings/`;
        this.getNationalitiesUrl = `${this.ppbApi}api/geo/countries/`;

        this.requestConfig = {
            headers: {
                Authorization: `Token ${this.token}`
            }
        };

        let requestConfig = this.requestConfig;
        if (this.$root.cookie.acreage != "en") {
            requestConfig.headers["Accept-Language"] = "zh-Hans";
        }

        this.basicForm = this.userProfile;
        this.user_pic = this.userProfile.picture;
        this.ruleFormCopy = this.userProfile;
        this.customer_level = this.userProfile.customer_level;

        this.emailForm = { email: this.userProfile.email };
        this.phoneForm = { phoneNumber: `+${this.userProfile.phoneNumber}` };

        this.duplicateFields = {
            phoneNumber: this.userProfile.phoneNumber,
            email: this.userProfile.email,
            nationality: this.userProfile.nationality,
            nationalityName: this.userProfile.nationalityName
        };

        this.validateFields.defaultCountryCode = this.userProfile.nationality;
    },

    mounted() {},

    methods: {
        onTelInput({ number, isValid, country }) {
            this.validateFields.phoneIllegal = false;

            if (!isValid) {
                this.validateFields.phoneIllegal = true;
                return (this.validateFields.display_phone_verification_input = false);
            }

            this.phoneNumberInput = number.replace(/\+|\s/g, "");

            if (this.phoneNumberInput == this.duplicateFields.phoneNumber) {
                return (this.validateFields.display_phone_verification_input = false);
            }

            return (this.validateFields.display_phone_verification_input = true);
        },
        disabledDate(time) {
            return time.getTime() > Date.now() - 8.64e6;
        },

        // set time out to disabled
        timeCountDown(that, timeout, field, countingDown) {
            const $t = this.$t.bind(this);
            setTimeout(() => {
                that.validateFields[field] = $t("profile.resend_code", {
                    timeout: timeout
                });

                timeout--;

                if (timeout > 0) {
                    this.timeCountDown(that, timeout, field, countingDown);
                } else {
                    that.validateFields[countingDown] = false;
                }
            }, 1000);
        },

        clearCountDown(func) {
            clearTimeout(func);
        },

        // get validate code
        sendCode(evt) {
            let timeout,
                params = {},
                field,
                countingDown;
            if (evt == "phone") {
                params = {
                    phoneNumber: this.phoneNumberInput
                };
                timeout = 60;
                field = "phoneField";
                countingDown = "phoneCountingDown";
            } else {
                params = {
                    email: this.emailForm.email
                };
                timeout = 120;
                field = "emailField";
                countingDown = "emailCountingDown";
            }
            this.$http
                .post(`${this.ppbApi}/api/code/`, params, this.requestConfig)
                .then(resp => {
                    if (resp.data.status == "OK") {
                        this.triggerMessage("success", {
                            message: this.$t("profile.sent_code")
                        });
                        this.validateFields[countingDown] = true;
                        this.timeCountDown(this, timeout, field, countingDown);
                    } else {
                        this.triggerMessage("error", {
                            message: this.$t(
                                `profile.code_error.${resp.data.code}`
                            )
                        });
                    }
                })
                .catch(error => {
                    this.triggerMessage("error", {
                        message: error.data[Object.keys(error.data)].toString()
                    });
                });
        },

        submitForm(formName) {
            let params = this.$refs[formName].model;
            if (formName == "basicForm") {
                params = {
                    id: params.id,
                    first_name: params.first_name,
                    last_name: params.last_name,
                    birth: params.birth,
                    gender: params.gender,
                    cityResidence: params.cityResidence,
                    nationality: params.nationality,
                    nationalityName: params.nationalityName
                };
            } else if (formName == "membershipForm") {
                params = {
                    code: params.invitation_code
                };
            }

            this.$refs[formName].validate(valid => {
                if (!valid) {
                    if (formName == "basicForm") {
                        this.triggerMessage("error", {
                            message: this.$t("profile.fields_error")
                        });

                        return;
                    } else if (formName == "membershipForm") {
                        this.triggerMessage("error", {
                            message: this.$t("UJ.section10.inputIsEmpty")
                        });

                        return;
                    }
                }

                this.btnController[formName] = true;

                if (formName == "membershipForm") {
                    this.$http
                        .post(`${this.useCode}`, params, this.requestConfig)
                        .then(resp => {
                            if (resp.status == 204) {
                                this.$refs[formName].resetFields();
                                this.$refs[formName].clearValidate();
                                this.triggerMessage("success", {
                                    message: this.$t("UJ.section10.successMsg")
                                });
                            }
                        })
                        .catch(error => {
                            this.triggerMessage("error", {
                                message: this.$t("UJ.section10.codeExist1")
                            });
                            this.$refs[formName].resetFields();
                            this.$refs[formName].clearValidate();
                        });

                    this.btnController[formName] = false;

                    return;
                }

                this.$http
                    .patch(this.reqUrl, params, this.requestConfig)
                    .then(rep => {
                        if (rep.status == 200) {
                            //reset form
                            if (formName == "pwdForm") {
                                this.$refs[formName].resetFields();
                                this.triggerMessage("success", {
                                    message: this.$t("profile.password_success")
                                });
                            } else {
                                this.triggerMessage("success", {
                                    message: this.$t("profile.basic_success")
                                });
                            }
                            this.$refs[formName].clearValidate();

                            this.$emit("getUserInfo", rep.data);
                        } else {
                            if (formName == "pwdForm") {
                                this.triggerMessage("error", {
                                    message: this.$t("profile.password_error")
                                });
                            } else {
                                this.triggerMessage("error", {
                                    message: this.$t("profile.basic_error")
                                });
                            }
                        }

                        this.btnController[formName] = false;
                    })
                    .catch(error => {
                        if (formName == "pwdForm") {
                            this.$refs[formName].clearValidate();
                            this.btnController[formName] = false;
                        }
                        this.triggerMessage("error", {
                            message: this.$t("profile.password_illegal")
                        });
                    });
            });
        },

        resetForm(formName) {
            this.$refs[formName].resetFields();
        },

        uploadAvatar() {
            if (!window.FileReader) {
                this.triggerMessage("error", {
                    message: this.$t("profile.file_reader")
                });
                return;
            }

            this.uploadTrigger();
        },

        uploadTrigger(event) {
            if (event.target.files.length != 1) return;
            let file = event.target.files[0];
            // Limit Size 4 MB
            if (file.size / 1024 > 4096) {
                this.triggerMessage("error", {
                    message: this.$t("profile.upload_error")
                });
                return;
            }
            let filename = file.name;
            let fileReader = new FileReader();

            if (file) {
                fileReader.readAsDataURL(file);
            }

            fileReader.onload = () => {
                let base64Str = fileReader.result;

                this.btnController.uploadAvatar = true;

                this.$http
                    .put(
                        this.reqUrl,
                        {
                            picture: base64Str,
                            id: this.userId
                        },
                        this.requestConfig
                    )
                    .then(rep => {
                        if (rep.status == 200) {
                            this.user_pic = base64Str;
                            this.$emit("getUserInfo", rep.data);
                            this.triggerMessage("success", {
                                message: this.$t("profile.upload_success")
                            });

                            $(".nav-user-avatar").css(
                                "background-image",
                                `url(${base64Str}`
                            );
                        } else {
                            this.triggerMessage("error", {
                                message: this.$t("profile.upload_error")
                            });
                        }
                        this.btnController.uploadAvatar = false;
                    });
            };
        },

        removeAvatar() {
            this.$http
                .put(
                    `${this.reqUrl}`,
                    {
                        picture: "",
                        id: this.userId
                    },
                    this.requestConfig
                )
                .then(rep => {
                    if (rep.status == 200) {
                        this.triggerMessage("success", {
                            message: this.$t("profile.remove_success")
                        });
                    } else {
                        this.triggerMessage("error", {
                            message: this.$t("profile.remove_error")
                        });
                    }
                })
                .catch(error => {
                    this.triggerMessage("error", {
                        message: error.data[Object.keys(error.data)].toString()
                    });
                });
        },

        triggerMessage(type, options) {
            options.duration = 6000;
            return Message[type](options);
        }
    }
};
</script>

<style lang="scss">
@media (min-width: 320px) {
    .el-select-dropdown {
        .el-select-dropdown__item.selected {
            color: #3b6bbf;
        }
    }
    .profile-container {
        .basic-form {
            .el-form-item.is-success .el-input__validateIcon {
                color: #3b6bbf;
            }

            .el-select .el-input.is-focus .el-input__inner {
                border-color: #3b6bbf;
            }

            .el-form-item.is-success .el-input__inner,
            .el-form-item.is-success .el-input__inner:focus,
            .el-form-item.is-success .el-textarea__inner,
            .el-form-item.is-success .el-textarea__inner:focus {
                border-color: var(--windows-blue);
            }

            .inline-input {
                width: 200px;
                vertical-align: middle;
            }
            .setTimeOut-style {
                margin-top: 15px;
                cursor: pointer;
                min-width: 100px;
                width: auto;
                height: 40px;
                border-radius: 4px;
                border: solid 1.9px var(--windows-blue);
                font-family: GothamMedium;
                font-size: 12px;
                font-weight: 500;
                color: var(--windows-blue);
            }
            .input-hint-plain {
                margin-left: 80px;
                margin-top: -7px;
                margin-bottom: 25px;
                // min-height: 70px;
                height: auto;
                border-radius: 5px;
                border: solid 1px #9b9b9b;
                padding: 15px 25px;
                font-family: GothamBook;
                font-size: 14px;
                line-height: 20px;
                color: #333333;
                b {
                    font-weight: 500;
                    font-family: GothamMedium;
                }
            }
        }
        .border-bottom {
            border-bottom: 1px solid #ebebeb;
        }
        .el-input__inner {
            font-family: GothamBook;
            font-size: 14px;
            color: var(--brownish-grey);
            height: 40px;
            line-height: 40px;
            border-radius: 5px;
            border: solid 1px #e0e7ff;
            background-color: rgba(224, 231, 255, 0.2);
        }
        .el-form-item {
            display: flex;
            align-items: center;
            margin-bottom: 25px;
            &.form-item-align {
                align-items: flex-start;
            }
            .el-form-item__label {
                font-family: GothamBook;
                font-size: 12px;
                text-align: right;
                color: var(--brownish-grey);
                max-width: 66px;
                line-height: 1.42;
                margin-right: 12px;
                padding-right: 0;
            }
            .el-form-item__content {
                margin-left: 0 !important;
                width: 100%;
            }
            &.is-required:not(.is-no-asterisk) > .el-form-item__label:before {
                content: unset;
            }

            .el-input,
            .el-select {
                width: 100%;
            }
            .vue-tel-input {
                height: 37px;
                border: 1px solid #e0e7ff;
                box-shadow: none;
                .dropdown {
                    background-color: rgba(224, 231, 255, 0.6);
                    outline: none;
                }
                input {
                    font-family: GothamBook;
                    font-size: 14px;
                    color: var(--brownish-grey);
                    background-color: rgba(224, 231, 255, 0.2);
                }
            }
        }
        .commitBtn {
            width: 150px;
            border: 0;
            height: 36px;
            line-height: 36px;
            padding-top: 0;
            padding-bottom: 0;
            border-radius: 4px;
            background-color: var(--windows-blue);
            font-family: GothamMedium;
            font-size: 14px;
            font-weight: 500;
            line-height: 21px;
            text-align: center;
            color: var(--white);
        }
        .pwd-form {
            h3 {
                height: 30px;
                line-height: 30px;
                font-family: GothamBook;
                font-size: 18px;
                color: #2e384d;
                margin: 25px 0;
                font-weight: normal;
            }
        }
        .el-form-item.form-item-align {
            align-items: center;
        }
        .invitation-form {
            h3 {
                line-height: 30px;
                font-family: GothamBook;
                font-size: 18px;
                color: #2e384d;
                margin: 20px 0 0;
                font-weight: normal;
            }
            .subtitle {
                font-family: GothamBook;
                font-size: 12px;
                line-height: 1.42;
                color: var(--brownish-grey);
                margin-bottom: 25px;
            }
            .el-input__inner {
                width: 100%;
            }
        }
    }

    .upload-box {
        display: flex;
        margin: 25px 0;
        align-items: center;
        .user-avatar {
            overflow: hidden;
            border-radius: 50%;
            width: 75px;
            height: 75px;
            margin-right: 14px;
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        .extra-upload {
            .inline-block {
                position: relative;
                display: inline-block;
                cursor: pointer;
            }
            .upload-file {
                opacity: 0;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                z-index: 1;
                display: inline-block;
            }
            .el-button {
                border: 0;
                height: 36px;
                border-radius: 4px;
                background-color: var(--windows-blue);
                font-family: GothamMedium;
                font-size: 12px;
                font-weight: 500;
                text-align: center;
                color: var(--white);
            }
            .el-button--plain {
                background-color: #aaaaaa;
            }
            .upload-tip {
                margin-top: 3px;
                font-family: GothamBook;
                font-size: 10px;
                line-height: 10px;
                color: var(--brownish-grey);
            }
        }
    }
}
@media (min-width: 768px) {
    .upload-box {
        margin: 25px 0 25px 123px;
    }
    .profile-container .el-form-item {
        .el-form-item__label {
            max-width: unset;
        }
        .el-form-item__content {
            margin-left: 130px;
        }
        .el-input {
            width: 100%;
        }
        .el-date-editor {
            width: 200px;
        }
        .inline-input {
            width: 200px;
        }
        .el-select {
            width: 200px;
        }
    }

    .profile-container .basic-form {
        .date-of-birth .el-input__inner {
            width: 200px;
        }
        .input-hint-plain {
            margin-left: 110px;
        }
        .setTimeOut-style {
            margin-top: 0;
        }
    }

    .profile-container .invitation-form .el-input__inner {
        width: 200px;
    }
}

@media (min-width: 992px) {
    .profile-container .basic-form .input-hint-plain {
        margin-left: 123px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/account/profileContainer.vue